<template>
  <div class="flex">
    <div class="flex">
      <filter-badge :label="botBadgeLabel"
                    :label-classes="'w-44 truncate'"
                    data-cy="botId-filter-badge"
                    class="ml-3"
                    @click.native="onBadgeClick(FILTER_ITEMS.BOT)"
      >
        <bot-icon/>
      </filter-badge>
      <date-range-filter-badge @toggle-date-from-picker="onBadgeClick(FILTER_ITEMS.DATE_FROM)"
                               @toggle-date-to-picker="onBadgeClick(FILTER_ITEMS.DATE_TO)"
                               class="ml-3"/>
      <filter-badge v-if="showGranularity"
                    :label="granularityLabel"
                    data-cy="granularity-filter-badge"
                    class="ml-3 pr-3"
                    @click.native="onBadgeClick(FILTER_ITEMS.GRANULARITY)"
      >
        <granularity-icon/>
      </filter-badge>
    </div>
    <span v-if="displayFilterBadgeSeparator" class="h-11/12 w-px bg-gray-a10 ml-3"/>

    <outcome-filter-badge v-if="displayOutcomesBadge"
                          class="ml-3"
                          :outcome-options="outcomeOptions"
                          :request-outcomes="requestOutcomes"
                          label="Outcomes:"
                          label-empty="No outcome selected"
                          :filter-item="FILTER_ITEMS.REQUEST_OUTCOME"
                          @click.native="onBadgeClick(FILTER_ITEMS.REQUEST_OUTCOME)"
                          @remove="$emit('badge-remove', $event)"
    />
    <outcome-filter-badge v-if="displayResolutionBadge"
                          class="ml-3"
                          :outcome-options="resolutionOptions"
                          :request-outcomes="assistanceResolutions"
                          label="Resolutions:"
                          label-empty="No resolution selected"
                          :filter-item="FILTER_ITEMS.ASSISTANCE_RESOLUTIONS"
                          @click.native="onBadgeClick(FILTER_ITEMS.ASSISTANCE_RESOLUTIONS)"
                          @remove="$emit('badge-remove', $event)"
    />
    <markets-filter-badge v-if="displayMarketBadge"
                          class="ml-3"
                          :markets="markets"
                          :market-options="marketOptions"
                          @click.native="onBadgeClick(FILTER_ITEMS.MARKETS)"
                          @remove="$emit('badge-remove', $event)"
    />
    <channels-filter-badge v-if="displayChannelBadge"
                           class="ml-3 test-bot-filter-badge"
                           :channels="channels"
                           :channel-options="channelOptions"
                           @click.native="onBadgeClick(FILTER_ITEMS.CHANNELS)"
                           @remove="$emit('badge-remove', $event)"
    />
    <filter-badge v-if="showAnswer && answerLabel"
                  :label="answerLabel"
                  class="ml-3 pr-3 test-bot-filter-badge"
                  :closeable="true"
                  @click.native="onBadgeClick(FILTER_ITEMS.ANSWER_ID)"
                  @remove="$emit('badge-remove', { [FILTER_ITEMS.ANSWER_ID]: null })"
    >
      <vs-icon icon="messages" size="1.2rem" class="mt-0.5"/>
    </filter-badge>
  </div>
</template>

<script>
import FilterBadge from '@/components/analytics/filter/FilterBadge'
import DateRangeFilterBadge from '@/components/analytics/filter/DateRangeFilterBadge'
import OutcomeFilterBadge from './OutcomeFilterBadge.vue'
import ChannelsFilterBadge from '@/components/analytics/filter/ChannelsFilterBadge'
import MarketsFilterBadge from '@/components/analytics/filter/MarketsFilterBadge.vue'
import BotIcon from '@/assets/images/icons/BotIcon'
import GranularityIcon from '@/assets/images/icons/GranularityIcon'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import channelModule from '@/store/modules/channel.module'
import outcomeModule from '@/store/modules/outcome.module'
import isEmpty from 'ramda/src/isEmpty'
import { FILTER_ITEMS, GRANULARITY_OPTIONS } from '@/utils/filter.constants'
import answerModule from '@/store/modules/answer.module'
import { getMarketsForLogicalBots, getLogicalBotId, getLogicalBotOptions } from '@/utils/market.utils'

export default {
  name: 'FilterBar',

  components: {
    GranularityIcon,
    FilterBadge,
    DateRangeFilterBadge,
    OutcomeFilterBadge,
    ChannelsFilterBadge,
    MarketsFilterBadge,
    BotIcon
  },

  props: {
    showRequestOutcomes: {
      type: Boolean,
      default: false
    },
    showAssistanceResolutions: {
      type: Boolean,
      default: false
    },
    showChannels: {
      type: Boolean,
      default: false
    },
    showMarkets: {
      type: Boolean,
      default: false
    },
    showGranularity: {
      type: Boolean,
      default: false
    },
    showAnswer: {
      type: Boolean,
      default: false
    },
    useLogicalBots: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      FILTER_ITEMS
    }
  },

  computed: {
    dateFrom: analyticsFilterModule.getDateFrom,
    dateTo: analyticsFilterModule.getDateTo,
    requestOutcomes: analyticsFilterModule.getRequestOutcomes,
    assistanceResolutions: analyticsFilterModule.getAssistanceResolutions,
    channels: analyticsFilterModule.getChannels,
    markets: analyticsFilterModule.getMarkets,
    channelOptions: channelModule.getChannelList,
    outcomeOptions: outcomeModule.getOutcomeList,
    resolutionOptions: outcomeModule.getResolutionList,
    bot: analyticsFilterModule.getBot,
    logicalBots: analyticsFilterModule.getLogicalBots,
    granularity: analyticsFilterModule.getGranularity,
    answerId: analyticsFilterModule.getAnswerId,
    answerList: answerModule.getAnswerList,

    displayChannelBadge() {
      return (
        this.showChannels &&
        this.channels !== null &&
        this.channelOptions.length !== this.channels.length
      )
    },

    // logicalBots === null means "the anchor bot only" (default)
    selectedLogicalBotIds() {
      if (this.logicalBots !== null && this.logicalBots !== undefined) return this.logicalBots

      const anchorLogicalBotId = getLogicalBotId(this.bot)
      return anchorLogicalBotId ? [anchorLogicalBotId] : []
    },

    botBadgeLabel() {
      if (!this.useLogicalBots || this.logicalBots === null || this.logicalBots === undefined) {
        return this.bot.name
      }

      const options = getLogicalBotOptions()
      const checkedOptions = options.filter(({ value }) => this.logicalBots.includes(value))
      if (!checkedOptions.length) return 'No bots selected'
      if (checkedOptions.length === options.length) return 'All bots'
      if (checkedOptions.length > 1) return `${checkedOptions.length} bots`
      return checkedOptions[0].text
    },

    marketOptions() {
      return getMarketsForLogicalBots(this.selectedLogicalBotIds)
    },

    displayMarketBadge() {
      return (
        this.showMarkets &&
        this.marketOptions.length > 0 &&
        this.markets !== null &&
        this.marketOptions.length !== this.markets.length
      )
    },

    displayOutcomesBadge() {
      return (
        this.showRequestOutcomes &&
        this.requestOutcomes !== null &&
        this.outcomeOptions.length !== this.requestOutcomes.length
      )
    },

    displayResolutionBadge() {
      return (
        this.showAssistanceResolutions &&
        this.assistanceResolutions !== null &&
        this.resolutionOptions.length !== this.assistanceResolutions.length
      )
    },

    displayFilterBadgeSeparator() {
      return (
        (this.showRequestOutcomes && this.requestOutcomes) ||
        this.displayChannelBadge ||
        this.displayMarketBadge
      )
    },

    granularityLabel() {
      return GRANULARITY_OPTIONS.find(({ id }) => id === this.granularity).name
    },

    answerLabel() {
      return this.answerList.find(({ id }) => id === this.answerId)?.name
    }
  },

  methods: {
    isEmpty,
    onBadgeClick(type) {
      this.$emit('badge-click', type)
    }
  }
}
</script>
